import { BrowseLink } from 'src/components/molecules/FileUploadDropZone/FileUploadDropZone.styles';
import { textStyleCss } from 'src/theme/atlas';
import styled, { css } from 'styled-components';

export const DynamicQuestionLabel = styled.label`
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
`;

export const DynamicQuestionDescription = styled.div`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.caption)};
  color: ${(props) => props.theme.atlas.semantic.color.type.muted};
  margin-bottom: 8px;
`;

export const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const DateTimeRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ReadOnlyCard = styled.div`
  border: 1px solid
    ${(props) =>
      props.theme.atlas.semantic.color.action.secondary.disabledOutline};
  border-radius: 6px;
  padding: ${(props) => props.theme.atlas.core.spacing[3]};
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  background-color: ${(props) =>
    props.theme.atlas.semantic.color.surface.default};
`;

export const DatabaseFieldModeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`;

export const ExternalOffenderChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

export const ExternalOffenderChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: ${(props) => props.theme.newPalette.neutral.N200};
  ${(props) => props.theme.newTypography.textStyleBodySmall};
  color: ${(props) => props.theme.newPalette.neutral.N800};
`;

export const ExternalOffenderChipRemove = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  color: ${(props) => props.theme.newPalette.neutral.N600};

  &:hover {
    color: ${(props) => props.theme.newPalette.neutral.N800};
  }
`;

export const ReadOnlyValue = styled.div`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)};
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  white-space: pre-wrap;
  word-break: break-word;
`;

export const ReadOnlyValueWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)};
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
`;

export const ReadOnlyFileList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ReadOnlyFileListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)};
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
`;

const fileNameTruncationCss = css`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

export const ReadOnlyFileName = styled.span`
  ${fileNameTruncationCss}
`;

export const ReadOnlyFileButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  color: inherit;
  text-align: start;
`;

export const DownloadFileName = styled(BrowseLink)`
  ${fileNameTruncationCss}
  max-width: 100%;
`;

export const ReadOnlyDataGridTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.caption)};
`;

export const ReadOnlyDataGridTh = styled.th`
  text-align: start;
  padding: 8px 12px 8px 0;
  color: ${(props) => props.theme.atlas.semantic.color.type.muted};
  font-weight: 600;
  border-bottom: 1px solid
    ${(props) => props.theme.atlas.semantic.color.outline.default};
`;

export const ReadOnlyDataGridTd = styled.td`
  padding: 8px 12px 8px 0;
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  border-bottom: 1px solid
    ${(props) => props.theme.atlas.semantic.color.ui.divider.default};
`;

export const DynamicQuestionFileUploadContainer = styled.div`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

export const DynamicQuestionFileUploadShell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;

  & > * {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  & > *:first-child {
    z-index: 0;
  }

  & > *:nth-child(2) {
    margin-top: 16px;
    position: relative;
    z-index: 1;
  }

  & .filepond--wrapper {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  & .filepond--root {
    margin-bottom: 0;
    width: 100% !important;
    max-width: 100%;
    min-width: 0;
  }

  &
    > *:first-child:has(
      .filepond--item:not([data-filepond-item-state='idle']):not(
          [data-filepond-item-state='processing-complete']
        )
    )
    .filepond--root {
    position: relative;
    z-index: 1;
  }

  & .filepond--panel-root {
    border: 1px dashed
      ${(props) => props.theme.atlas.semantic.color.outline.default};
    border-radius: ${(props) => props.theme.atlas.core.radius.lg};
    background-color: ${(props) =>
      props.theme.atlas.semantic.color.surface.default} !important;
    box-sizing: border-box;
    overflow: hidden;
  }

  & > *:first-child:hover .filepond--panel-root {
    border-color: ${(props) => props.theme.atlas.semantic.color.outline.hover};
    background-color: ${(props) =>
      props.theme.atlas.semantic.color.surface.variant} !important;
  }

  & .filepond--drop-label {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    padding: 32px 24px;
    min-height: 160px;
    text-align: center;
    cursor: pointer;
    border: none;
    background-color: transparent;
    ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)};
    color: ${(props) => props.theme.atlas.semantic.color.type.default};
  }

  & .filepond--list-scroller {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  & .filepond--list-scroller:not(:has(.filepond--item)),
  & .filepond--list:not(:has(.filepond--item)) {
    pointer-events: none;
  }

  & .filepond--list:has(.filepond--item) {
    padding: 0 12px 16px;
  }

  & .filepond--drop-label .filepond--label-action {
    color: ${(props) => props.theme.atlas.semantic.color.type.default};
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }

  & .filepond--drop-label .filepond--label-action:focus-visible {
    outline: 2px solid
      ${(props) => props.theme.atlas.semantic.color.ui.focusRing};
    outline-offset: 2px;
    border-radius: 2px;
  }

  & .filepond--drop-label .dynamic-question-file-upload-hint {
    ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.caption)};
    color: ${(props) => props.theme.atlas.semantic.color.type.muted};
    margin: 0;
    user-select: none;
  }

  & .dynamic-question-file-upload-icon {
    color: ${(props) => props.theme.atlas.semantic.color.type.muted};
    font-size: 32px;
    line-height: 1;
  }

  & .filepond--drop-label p:first-of-type {
    margin: 0;
    user-select: none;
  }
`;
